var _styled;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'react-emotion';
import { BLACK, YELLOW, ERROR_COLOUR } from 'govuk-colours';
import { FONT_SIZE, LINE_HEIGHT, MEDIA_QUERIES, NTA_LIGHT, SPACING } from '@govuk-react/constants';
import Label from '@govuk-react/label';
import LabelText from '@govuk-react/label-text';
import ErrorText from '@govuk-react/error-text';
import HintText from '@govuk-react/hint-text';
import { withWhiteSpace } from '@govuk-react/hoc';
var StyledContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column'
}, function (_ref) {
  var errorText = _ref.errorText;
  return {
    borderLeft: errorText ? "4px solid " + ERROR_COLOUR : undefined,
    marginRight: errorText ? SPACING.SCALE_3 : undefined,
    paddingLeft: errorText ? SPACING.SCALE_2 : undefined
  };
});
var StyledInput = styled('input')((_styled = {
  boxSizing: 'border-box',
  fontFamily: NTA_LIGHT,
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  fontWeight: 400,
  textTransform: 'none',
  fontSize: FONT_SIZE.SIZE_16,
  lineHeight: LINE_HEIGHT.SIZE_16
}, _styled[MEDIA_QUERIES.LARGESCREEN] = {
  fontSize: FONT_SIZE.SIZE_19,
  lineHeight: LINE_HEIGHT.SIZE_19
}, _styled.width = '100%', _styled.padding = '5px 4px 4px', _styled.border = "2px solid " + BLACK, _styled[':focus'] = {
  outline: "3px solid " + YELLOW,
  outlineOffset: 0
}, _styled), function (_ref2) {
  var errorText = _ref2.errorText;
  return {
    border: errorText ? "4px solid " + ERROR_COLOUR : "2px solid " + BLACK
  };
});
var StyledList = styled('div')({
  fontFamily: NTA_LIGHT,
  display: 'flex',
  '> label': {
    width: '50px',
    marginRight: '20px',
    marginBottom: 0
  },
  '> label.year': {
    width: '70px'
  }
});
/**
 *
 * ### Usage
 *
 * Simple
 * ```jsx
 * <DateInput>What is your date of birth?</DateInput>
 * ```
 *
 * Date with hint text
 * ```jsx
 * <DateInput hintText="For example, 31 03 1980">
 *   What is your date of birth?
 * </DateInput>
 * ```
 *
 * Date with hint text & error
 * ```jsx
 * <DateInput
 *   hintText="For example, 31 03 1980"
 *   errorText="Error message goes here"
 * >
 *   What is your date of birth?
 * </DateInput>
 * ```
 *
 * With custom input name props
 * ```jsx
 * <DateInput inputNames={{ day: 'dayInputName' }} hintText="For example, 31 03 1980">
 *   What is your date of birth?
 * </DateInput>
 * ```
 *
 * ### References:
 * - https://github.com/alphagov/govuk-frontend/tree/master/src/components/date-input
 *
 */

var DateInput = function DateInput(_ref3) {
  var children = _ref3.children,
      errorText = _ref3.errorText,
      hintText = _ref3.hintText,
      _ref3$inputNames = _ref3.inputNames,
      _ref3$inputNames$day = _ref3$inputNames.day,
      day = _ref3$inputNames$day === void 0 ? 'dateInputDay' : _ref3$inputNames$day,
      _ref3$inputNames$mont = _ref3$inputNames.month,
      month = _ref3$inputNames$mont === void 0 ? 'dateInputMonth' : _ref3$inputNames$mont,
      _ref3$inputNames$year = _ref3$inputNames.year,
      year = _ref3$inputNames$year === void 0 ? 'dateInputYear' : _ref3$inputNames$year,
      _ref3$defaultValues = _ref3.defaultValues,
      _ref3$defaultValues$d = _ref3$defaultValues.defaultDay,
      defaultDay = _ref3$defaultValues$d === void 0 ? '' : _ref3$defaultValues$d,
      _ref3$defaultValues$d2 = _ref3$defaultValues.defaultMonth,
      defaultMonth = _ref3$defaultValues$d2 === void 0 ? '' : _ref3$defaultValues$d2,
      _ref3$defaultValues$d3 = _ref3$defaultValues.defaultYear,
      defaultYear = _ref3$defaultValues$d3 === void 0 ? '' : _ref3$defaultValues$d3,
      props = _objectWithoutProperties(_ref3, ["children", "errorText", "hintText", "inputNames", "defaultValues"]);

  return React.createElement(StyledContainer, _extends({}, props, {
    errorText: errorText
  }), React.createElement(LabelText, {
    errorText: errorText
  }, children), hintText ? React.createElement(HintText, null, hintText) : React.createElement("span", null), errorText ? React.createElement(ErrorText, {
    errorText: errorText
  }, errorText) : React.createElement("span", null), React.createElement(StyledList, null, React.createElement(Label, null, React.createElement(LabelText, null, "Day"), React.createElement(StyledInput, {
    name: day,
    errorText: errorText,
    type: "text",
    defaultValue: defaultDay
  })), React.createElement(Label, null, React.createElement(LabelText, null, "Month"), React.createElement(StyledInput, {
    name: month,
    errorText: errorText,
    type: "text",
    defaultValue: defaultMonth
  })), React.createElement(Label, {
    className: "year"
  }, React.createElement(LabelText, null, "Year"), React.createElement(StyledInput, {
    name: year,
    errorText: errorText,
    type: "text",
    defaultValue: defaultYear
  }))));
};

DateInput.defaultProps = {
  hintText: undefined,
  errorText: undefined,
  inputNames: {
    day: undefined,
    month: undefined,
    year: undefined
  },
  defaultValues: {
    defaultDay: undefined,
    defaultMonth: undefined,
    defaultYear: undefined
  }
};
export default withWhiteSpace({
  marginBottom: 6
})(DateInput);
//# sourceMappingURL=index.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@govuk-react/date-input/es/index.js
// module id = 155
// module chunks = 0